import { ref } from 'vue';
import { injectStrict } from '@/injection/injectStrict.ts';
import { AuthKey } from '@/injection/keys.ts';
import Auth from '@/classes/Auth.ts';
import UserRepository from '@/repositories/user/UserRepository.ts';
import {
    CurrentUser,
    User,
    UserCreateForm,
    UserNotificationSetting,
    UserNotificationSettingUpdateForm,
    UsersPaginator,
    UsersPaginatorItem,
    UsersRequestFilter,
    UserUpdateForm,
} from '@/types/user.type.ts';
import { PaginationMeta } from '@/types/pagination-meta.ts';
import { DealerListItem } from '@/types/dealer.type.ts';
import useLoading from '@/utils/useLoading.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useUserRepository() {
    const arUsers = ref<UsersPaginatorItem[]>([]);
    const usersPaginationMeta = ref<PaginationMeta | null>(null);
    const { isLoading: isUsersLoading, setLoading: setUsersLoading } = useLoading();
    const { isLoading: isCurrentUserDealersLoading, setLoading: setCurrentUserDealersLoading } = useLoading();
    const { isLoading: isSettingsLoading, setLoading: setSettingsLoading } = useLoading();
    const userRepository = new UserRepository();

    const $Auth: Auth = injectStrict<Auth>(AuthKey);

    function getUsers(requestFilter?: UsersRequestFilter): void {
        setUsersLoading(true);

        userRepository.getUsers(requestFilter)
            .then((usersPaginator: UsersPaginator) => {
                arUsers.value = usersPaginator.data;
                usersPaginationMeta.value = usersPaginator.meta;

                setUsersLoading(false);
            });
    }

    function getUser(id: number): Promise<User> {
        return userRepository.getUser(id);
    }

    function createUser(createForm: UserCreateForm): Promise<void> {
        return userRepository.createUser(createForm);
    }

    function updateUser(id: number, updateForm: UserUpdateForm): Promise<void> {
        return userRepository.updateUser(id, updateForm);
    }

    function activateUser(id: number): Promise<void> {
        return userRepository.activateUser(id);
    }

    function deactivateUser(id: number): Promise<void> {
        return userRepository.deactivateUser(id);
    }

    function approveUser(id: number): Promise<void> {
        return userRepository.approveUser(id);
    }

    function rejectUser(id: number): Promise<void> {
        return userRepository.rejectUser(id);
    }

    function getUserDealers(id: number): Promise<DealerListItem[]> {
        return userRepository.getUserDealers(id);
    }

    function getCurrentUser(): CurrentUser {
        return $Auth.getCurrentUser();
    }

    function getCurrentUserDealers(): Promise<DealerListItem[]> {
        setCurrentUserDealersLoading(true);

        return getUserDealers($Auth.getCurrentUser().id)
            .then((dealers) => {
                setCurrentUserDealersLoading(false);

                return dealers;
            });
    }

    function changeUserCurrentDealer(id: number): Promise<void> {
        return new Promise<void>((resolve) => {
            userRepository.changeUserCurrentDealer(id).then(() => {
                $Auth.fetchCurrentUser().then(() => {
                    window.location.reload();

                    resolve();
                });
            });
        });
    }

    function getUserNotificationSettings(): Promise<UserNotificationSetting[]> {
        setSettingsLoading(true);

        return userRepository.getUserNotificationSettings()
            .then((settings) => {
                setSettingsLoading(false);

                return settings;
            });
    }

    function updateUserNotificationSettings(...forms: UserNotificationSettingUpdateForm[]): Promise<void> {
        return userRepository.updateUserNotificationSettings({
            notification_settings: forms,
        });
    }

    return {
        arUsers,
        usersPaginationMeta,
        isUsersLoading,
        isCurrentUserDealersLoading,
        isSettingsLoading,
        getUsers: getUsers,
        getUser: getUser,
        createUser: createUser,
        updateUser: updateUser,
        activateUser: activateUser,
        deactivateUser: deactivateUser,
        approveUser: approveUser,
        rejectUser: rejectUser,
        getUserDealers: getUserDealers,
        getCurrentUser: getCurrentUser,
        getCurrentUserDealers: getCurrentUserDealers,
        changeUserCurrentDealer: changeUserCurrentDealer,
        getUserNotificationSettings: getUserNotificationSettings,
        updateUserNotificationSettings: updateUserNotificationSettings,
    };
}
